import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CompanyProvider, useCompany } from "@/config";
import { Toaster } from "sonner";
import "@/App.css";

import Layout from "@/components/Layout";
import SessionTimeoutBanner from "@/components/SessionTimeoutBanner";
import IdleLogoutTimer from "@/components/IdleLogoutTimer";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";

// Employee
import EmpDashboard from "@/pages/employee/Dashboard";
import EmpTimesheet from "@/pages/employee/Timesheet";
import EmpAttendance from "@/pages/employee/Attendance";
import EmpProjects from "@/pages/employee/MyProjects";
import EmpLeave from "@/pages/employee/Leave";

// Admin / Super Admin pages
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminEmployees from "@/pages/admin/Employees";
import AdminProjects from "@/pages/admin/Projects";
import AdminTimesheets from "@/pages/admin/Timesheets";
import AdminActivity from "@/pages/admin/DailyActivity";
import AdminAttendance from "@/pages/admin/Attendance";
import AdminMyAttendance from "@/pages/admin/MyAttendance";
import AdminLeave from "@/pages/admin/LeaveManagement";
import AdminReports from "@/pages/admin/Reports";
import AdminHolidays from "@/pages/admin/Holidays";
import AdminSettings from "@/pages/admin/Settings";
import AdminAudit from "@/pages/admin/AuditLogs";
import AdminPayroll from "@/pages/admin/Payroll";
import AdminSalaryStructures from "@/pages/admin/SalaryStructures";
import AdminSalaryComponents from "@/pages/admin/SalaryComponents";
import AdminLoans from "@/pages/admin/Loans";
import OrgHierarchy from "@/pages/admin/OrgHierarchy";
import TeamsPage from "@/pages/admin/Teams";
import PendingApprovals from "@/pages/admin/PendingApprovals";
import Directory from "@/pages/admin/Directory";
import NotificationPreferences from "@/pages/admin/NotificationPreferences";

// Employee
import EmpMySalary from "@/pages/employee/MySalary";
import EmpMyPayslips from "@/pages/employee/MyPayslips";
import EmpMyProfile from "@/pages/employee/MyProfile";

function Protected({ children, roles = null }) {
  const { user, loading } = useAuth();
  const company = useCompany();
  if (loading) return (
    <div className="min-h-screen grid place-items-center bg-[#F7F7F5]">
      <div className="flex flex-col items-center gap-4">
        <img src={company.brand.logo} alt={company.shortName} className="h-14 w-14 object-contain" />
        <div className="eyebrow text-neutral-500">Loading workspace…</div>
      </div>
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

const STAFF = ["admin", "super_admin"];
const SUPER = ["super_admin"];

function RoleHome() {
  const { user } = useAuth();
  return STAFF.includes(user?.role) ? <AdminDashboard /> : <EmpDashboard />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/" element={<Protected><RoleHome /></Protected>} />
      {/* Employee */}
      <Route path="/timesheet" element={<Protected><EmpTimesheet /></Protected>} />
      <Route path="/attendance" element={<Protected><EmpAttendance /></Protected>} />
      <Route path="/projects" element={<Protected><EmpProjects /></Protected>} />
      <Route path="/leave" element={<Protected><EmpLeave /></Protected>} />
      <Route path="/salary" element={<Protected><EmpMySalary /></Protected>} />
      <Route path="/payslips" element={<Protected><EmpMyPayslips /></Protected>} />
      <Route path="/profile" element={<Protected><EmpMyProfile /></Protected>} />
      <Route path="/preferences" element={<Protected><NotificationPreferences /></Protected>} />
      {/* Admin + Super Admin (team-scoped on the backend for admins) */}
      <Route path="/admin/approvals" element={<Protected roles={STAFF}><PendingApprovals /></Protected>} />
      <Route path="/admin/employees" element={<Protected roles={STAFF}><AdminEmployees /></Protected>} />
      <Route path="/admin/projects" element={<Protected roles={STAFF}><AdminProjects /></Protected>} />
      <Route path="/admin/timesheets" element={<Protected roles={STAFF}><AdminTimesheets /></Protected>} />
      <Route path="/admin/activity" element={<Protected roles={STAFF}><AdminActivity /></Protected>} />
      <Route path="/admin/attendance" element={<Protected roles={STAFF}><AdminAttendance /></Protected>} />
      <Route path="/admin/my-attendance" element={<Protected roles={["admin"]}><AdminMyAttendance /></Protected>} />
      <Route path="/admin/leave" element={<Protected roles={STAFF}><AdminLeave /></Protected>} />
      <Route path="/admin/reports" element={<Protected roles={STAFF}><AdminReports /></Protected>} />
      <Route path="/admin/hierarchy" element={<Protected roles={STAFF}><OrgHierarchy /></Protected>} />
      <Route path="/admin/directory" element={<Protected roles={STAFF}><Directory /></Protected>} />
      {/* Super Admin only */}
      <Route path="/admin/users" element={<Navigate to="/admin/employees" replace />} />
      <Route path="/admin/teams" element={<Protected roles={SUPER}><TeamsPage /></Protected>} />
      <Route path="/admin/holidays" element={<Protected roles={SUPER}><AdminHolidays /></Protected>} />
      <Route path="/admin/settings" element={<Protected roles={SUPER}><AdminSettings /></Protected>} />
      <Route path="/admin/audit" element={<Protected roles={STAFF}><AdminAudit /></Protected>} />
      <Route path="/admin/payroll" element={<Protected roles={SUPER}><AdminPayroll /></Protected>} />
      <Route path="/admin/salary-structures" element={<Protected roles={SUPER}><AdminSalaryStructures /></Protected>} />
      <Route path="/admin/salary-components" element={<Protected roles={SUPER}><AdminSalaryComponents /></Protected>} />
      <Route path="/admin/loans" element={<Protected roles={SUPER}><AdminLoans /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <CompanyProvider>
      <AuthProvider>
        <BrowserRouter>
          <SessionTimeoutBanner />
          <IdleLogoutTimer />
          <AppRoutes />
          <Toaster position="top-right" richColors closeButton />
        </BrowserRouter>
      </AuthProvider>
    </CompanyProvider>
  );
}
